import { Link } from "react-router-dom";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { Check, Sparkles, Star, Zap, Crown, ArrowRight } from "lucide-react";

const TIERS = [
  {
    id: "preview",
    label: "Preview",
    price: "$0",
    blurb: "Try the full wizard. Share with friends.",
    cta: "Start free",
    href: "/create",
    bullets: [
      "60-second 4-step wizard",
      "AI-generated logo (Nano Banana)",
      "AI-generated whitepaper (Claude)",
      "Shareable draft preview link",
      "Live (paper) price chart",
    ],
    locked: ["Listed on /explore", "Permanent URL", "Editable forever"],
    highlight: false,
  },
  {
    id: "publish",
    label: "Publish",
    price: "$19",
    blurb: "Permanent coin page, listed on /explore. One-time.",
    cta: "Publish a coin",
    href: "/create",
    bullets: [
      "Everything in Preview",
      "Permanent URL on /c/[slug]",
      "Listed on /explore (trending sort)",
      "Edit anytime",
      "Trade simulator unlocked publicly",
      "Likes + view counter",
    ],
    locked: [],
    highlight: true,
  },
  {
    id: "bundle",
    label: "Launch Bundle",
    price: "$29",
    blurb: "Publish + real on-chain mint + 24h boost. One-time.",
    cta: "Grab the bundle",
    href: "/create",
    bullets: [
      "Everything in Publish",
      "On-chain mint — platform fee waived",
      "24h Featured boost on /explore",
      "Real SPL token via one Phantom click",
      "Mint + freeze authority auto-revoked",
    ],
    locked: [],
    highlight: false,
    bundle: true,
  },
  {
    id: "feature",
    label: "Featured",
    price: "$49",
    blurb: "7 days at the top of /explore. Gold badge.",
    cta: "Feature your coin",
    href: "/explore",
    bullets: [
      "Everything in Publish",
      "Top of /explore for 7 days",
      "Gold 'Featured' badge",
      "~3× view multiplier (avg.)",
      "Lifetime stays as 'Published'",
    ],
    locked: [],
    highlight: false,
    accent: true,
  },
];

const LADDER = [
  {
    id: "boost",
    label: "Fresh Boost",
    price: "$9",
    duration: "24 hours",
    desc: "Jump back to the top of /explore trending. Perfect right after launch or a big update.",
    icon: Zap,
    color: "#00ff41",
  },
  {
    id: "feature",
    label: "Featured",
    price: "$49",
    duration: "7 days",
    desc: "Gold badge + top of /explore all week. ~3× view multiplier on average.",
    icon: Star,
    color: "#cfff04",
  },
  {
    id: "hero",
    label: "Homepage Hero",
    price: "$149",
    duration: "30 days",
    desc: "The most premium spot on SolSpur — your coin showcased on the homepage. One coin at a time.",
    icon: Crown,
    color: "#ffd700",
  },
];

const FAQ = [
  {
    q: "Do you actually deploy the coin on-chain?",
    a: "Yes — one click. After publishing, connect Phantom on your coin page and mint a real SPL token on Solana mainnet (with a free practice run on devnet first). The full supply lands in your wallet, and mint + freeze authority are revoked automatically so DEX scanners rate it safe.",
  },
  {
    q: "Can I edit my coin after publishing?",
    a: "Yes. Once paid, your coin page is editable forever from any browser — just sign in to your SolSpur account.",
  },
  {
    q: "Refunds?",
    a: "Within 24 hours of publishing, if your coin hasn't been viewed by anyone, full refund. After that, no refunds — the listing is non-revocable.",
  },
  {
    q: "What about ongoing fees?",
    a: "None. Publishing is one-time. Minting on mainnet costs Solana network rent (~0.02 SOL) plus a small 0.02 SOL SolSpur fee — waived entirely if you bought the $29 Launch Bundle. Devnet practice mints are always free.",
  },
  {
    q: "How does the Homepage Hero slot work?",
    a: "One coin at a time. $149 puts your coin front-and-center on the SolSpur homepage for 30 days, plus a Featured badge on /explore for the same period. First come, first served — if the slot is taken, your coin page shows exactly when it frees up.",
  },
];

function tierBorder(t) {
  if (t.highlight) return "border-[#cfff04]";
  if (t.accent) return "border-[#ffd700]";
  if (t.bundle) return "border-[#00ff41]";
  return "border-[#3a3a3a]";
}

function tierCtaClass(t) {
  if (t.highlight) return "btn-brutal bg-[#cfff04] text-[#050505]";
  if (t.accent) return "btn-brutal bg-[#ffd700] text-[#050505]";
  if (t.bundle) return "btn-brutal bg-[#00ff41] text-[#050505]";
  return "border border-[#3a3a3a] text-white hover:border-[#cfff04] hover:text-[#cfff04]";
}

export default function Pricing() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="pricing-page">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              // Pricing
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              Free to draft.
              <br />
              <span className="text-[#cfff04]">Cheap to launch.</span>
            </h1>
            <p className="text-[#e2e2e2] mt-4 max-w-xl mx-auto">
              No subscriptions. No surprise fees. Pay once when you're ready to publish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-16" data-testid="pricing-tiers">
            {TIERS.map((t) => (
              <div
                key={t.id}
                data-testid={`tier-${t.id}`}
                className={`border bg-[#121212] p-7 relative ${tierBorder(t)}`}
              >
                {t.highlight && (
                  <span className="absolute -top-3 left-7 bg-[#cfff04] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-2 py-0.5">
                    Most popular
                  </span>
                )}
                {t.bundle && (
                  <span className="absolute -top-3 left-7 bg-[#00ff41] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-2 py-0.5">
                    Best value
                  </span>
                )}
                {t.accent && (
                  <span className="absolute -top-3 left-7 bg-[#ffd700] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                    <Star size={10} fill="currentColor" /> Boost
                  </span>
                )}
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-2">
                  {t.label}
                </div>
                <div className="font-display text-5xl font-black mb-1">{t.price}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-4">
                  {t.id === "preview" ? "always" : "one-time"}
                </div>
                <p className="text-sm text-[#e2e2e2] mb-5">{t.blurb}</p>
                <ul className="space-y-2 mb-7">
                  {t.bullets.map((b) => (
                    <li key={b} className="text-sm flex gap-2">
                      <Check size={14} className="text-[#00ff41] flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                  {t.locked.map((b) => (
                    <li key={b} className="text-sm flex gap-2 text-[#d0d0d0]">
                      <span className="w-3.5 h-3.5 border border-[#444] flex-shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to={t.href}
                  data-testid={`tier-cta-${t.id}`}
                  className={`block text-center font-bold uppercase tracking-wider text-sm py-3 ${tierCtaClass(t)}`}
                >
                  {t.id === "publish" && <Sparkles size={14} className="inline mr-2" />}
                  {t.cta}
                </Link>
              </div>
            ))}
          </div>

          <div className="mb-16" data-testid="pricing-ladder">
            <div className="text-center mb-8">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ffd700] mb-2">
                // Already published?
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter">
                Climb the visibility ladder.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-5xl mx-auto">
              {LADDER.map((r) => (
                <div
                  key={r.id}
                  data-testid={`ladder-${r.id}`}
                  className="border border-[#3a3a3a] bg-[#121212] p-6 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <r.icon size={14} style={{ color: r.color }} />
                    <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                      {r.label}
                    </span>
                  </div>
                  <div className="font-display text-4xl font-black mb-0.5">{r.price}</div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] mb-4">
                    {r.duration}
                  </div>
                  <p className="text-sm text-[#e2e2e2] mb-6 flex-1">{r.desc}</p>
                  <Link
                    to="/my-coins"
                    data-testid={`ladder-cta-${r.id}`}
                    className="font-mono text-xs uppercase tracking-widest inline-flex items-center gap-1.5 hover:underline"
                    style={{ color: r.color }}
                  >
                    Promote a coin <ArrowRight size={12} />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <h2 className="font-display font-black text-3xl uppercase tracking-tighter mb-6">FAQ</h2>
            <div className="border-t border-[#3a3a3a]">
              {FAQ.map((f, i) => (
                <details key={f.q} className="border-b border-[#3a3a3a] py-5 group" data-testid={`pricing-faq-${i}`}>
                  <summary className="font-display text-lg font-bold uppercase tracking-tight cursor-pointer flex items-center justify-between gap-4 list-none">
                    {f.q}
                    <span className="text-[#d0d0d0] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                  </summary>
                  <p className="text-[#e2e2e2] mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
